import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled, { FlattenSimpleInterpolation, css, keyframes } from 'styled-components';
import { StyledButton, Alert } from 'components';
import COLOR from 'constants/color';
import { Link } from 'react-router-dom';

interface IconProps {
  color: string;
}

interface ColumnProps {
  columnStyle?: FlattenSimpleInterpolation;
  flex: string;
}

export const CustomCard = styled.div`
  border-radius: 0.5rem;
  border: 1px solid ${COLOR.neutral};
  padding: 1rem;
  background-color: ${COLOR.white};
  margin-bottom: 1.5rem;

  & > :not(:last-child) {
    margin-bottom: 1.5rem;
  }
`;

export const CardRow = styled.div`
  display: flex;

  & > * {
    margin-right: 1rem;
  }
`;

export const Heading = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 1.25rem 0 1.25rem 0;
`;

export const Icon = styled(FontAwesomeIcon)<IconProps>`
  color: ${(props): string => props.color};
  align-self: center;
  margin-right: 0.375rem;
`;

export const BulletPoint = styled(FontAwesomeIcon)`
  font-size: 6px;
  margin-left: 4px;
  margin-right: 4px;
`;

export const Description = styled.div`
  font-size: 0.875rem;
`;

export const Column = styled.div<ColumnProps>`
  display: flex;
  flex-direction: column;
  flex: ${(props): string => props.flex};

  ${(props): FlattenSimpleInterpolation => props.columnStyle}
`;

export const Container = styled.div`
  display: flex;
  margin-top: 1rem;
`;

export const Title = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  margin-right: auto;
  color: ${COLOR.darkGray};
`;

export const CancelButton = styled(StyledButton)`
  margin-left: 0.5rem;
`;

export const Prompt = styled.p`
  margin-bottom: 1.5rem;
`;

export const inlineTextInputStyle = css`
  display: inline-block;
  margin-right: 1rem;
`;

export const Row = styled.div`
  margin-bottom: 1.5rem;
`;

export const Field = styled.div`
  margin-bottom: 1rem;
`;

export const SquadIconContainer = styled.span`
  display: inline-flex;
  justify-content: center;
  margin-right: 0.3rem;
  background-color: ${COLOR.cyan};
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  padding-top: 0.3rem;
  padding-bottom: 0.3rem;
  border-radius: 20px;
`;

export const SquadIcon = styled(FontAwesomeIcon)`
  font-size: 0.75rem;
  color: ${COLOR.darkGray};
`;

export const ActionButton = styled(StyledButton)`
  margin-right: 0.5rem;
  &:last-child {
    margin-right: 0;
  }
`;

// Define the animation keyframes
const fadeAnimation = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const CardContainer = styled.div`
  animation: ${fadeAnimation} 0.3s ease-in-out;
  display: flex;
  margin-top: 1rem;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
  }
`;

export const StyledLink = styled(Link)`
  color: ${COLOR.blue};
  cursor: pointer;
  text-decoration: none;
`;

export const ActionButtons = styled.div`
  margin-bottom: 1.5rem;
`;

export const FieldInfo = styled.div`
  font-size: 0.8rem;
  color: ${COLOR.midDarkGrey};
`;

export const CustomLink = styled.a`
  font-size: 0.8rem;
  text-decoration: underline;
  color: ${COLOR.blue};
  cursor: pointer;
`;

export const ActionButtonsContainer = styled.div`
  width: auto;
  height: auto;
  display: flex;
  height: 2.2rem;
`;

export const DropdownButton = styled(StyledButton)`
  border-width: 0;
  background-color: ${COLOR.white};
  padding: 0 0;
  height: 2.75rem;

  :hover {
    background-color: ${COLOR.white};
    color: ${(props): string => (props.disabled ? COLOR.grey : COLOR.midDarkGrey)};
    border: none;
  }

  :focus {
    box-shadow: none;
  }

  :disabled {
    border: none;
  }
`;

export const DropdownButtonIcon = styled(FontAwesomeIcon)`
  margin-right: 0.375rem;
`;

export const AlertMessage = styled(Alert)`
  margin-bottom: 0.4rem;
`;

export const Required = styled.span`
  color: ${COLOR.red};
  margin-left: 0.2rem;
`;

export const BoldText = styled.div`
  font-weight: 600;
`;

interface StackProps {
  gap?: string;
  direction?: string;
  wrap?: boolean;
  mdScreenStyle?: FlattenSimpleInterpolation;
}

export const Stack = styled.div<StackProps>`
  display: flex;
  flex-wrap: ${(props): string => (props.wrap ? 'wrap' : 'nowrap')};
  flex-direction: ${(props): string => (props.direction ? props.direction : 'row')};
  gap: ${(props): string => props.gap && props.gap};
  @media (min-width: 768px) {
    ${(props): FlattenSimpleInterpolation => props.mdScreenStyle}
  }
`;

interface ColumnItemProps {
  mdScreenStyle?: FlattenSimpleInterpolation;
  order?: number;
}

export const ColumnItem = styled.div<ColumnItemProps>`
  order: ${(props): number => props.order && props.order};
  @media (min-width: 768px) {
    ${(props): FlattenSimpleInterpolation => props.mdScreenStyle}
    order: 0;
  }
`;

export const Card = styled.div`
  border-radius: 0.75rem;
  box-shadow: 0px 1px 1px 2px rgba(222, 222, 222, 0.06),
    0px 2px 6px 0px ${(props): string => props.theme.colors.gray_200};

  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
  align-self: stretch;
`;

export const CardWrapper = styled.div`
  @media (min-width: 767px) {
    max-width: 220px;
    min-width: 220px;
  }
`;

export const BorderIconWrapper = styled(StyledButton)`
  width: 36px;
  height: 36px;
  border-radius: 0.5rem;
  border: 1px solid ${(props): string => props.theme.colors.gray_300};
`;

interface DividerProps {
  isHidden?: boolean;
  marginTopBottom?: string;
  marginBottom?: string;
}

export const Divider = styled.hr<DividerProps>`
  margin: 0;
  height: 1px;
  width: 100%;
  border: none;
  background-color: ${(props): string => props.theme.colors.gray_200};
  visibility: ${(props): string => props.isHidden && 'hidden'};
  margin: ${(props): string =>
    props.marginTopBottom && `${props.marginTopBottom} 0 ${props.marginTopBottom} 0`};
  margin-bottom: ${(props): string => props.marginBottom && props.marginBottom};
`;

interface IconGrayish {
  isGray?: boolean;
  fontSize?: string;
}

export const IconGrayish = styled(FontAwesomeIcon)<IconGrayish>`
  color: ${(props): string =>
    props.isGray ? props.theme.colors.gray_500 : props.theme.colors.gray_700};
  font-size: ${(props): string => props.fontSize && props.fontSize};
`;

export const SuperScript = styled.span<{ lineHeight?: string }>`
  line-height: 131%;
  line-height: ${({ lineHeight }): string => (lineHeight ? lineHeight : '131%')};
`;

export const IsQaLabel = styled.span`
  border-radius: 1rem;
  background-color: ${(props): string => props.theme.colors.pink_50};
  color: ${(props): string => props.theme.colors.pink_700};
  padding: 0.125rem 0.5rem;
`;

export const LabelGray = styled.span`
  background-color: ${(props): string => props.theme.colors.gray_100};
  color: ${(props): string => props.theme.colors.gray_700};
  padding: 0.25rem 0.625rem;
  border-radius: 1rem;
  display: inline-block;
`;

export const OrderInfoContainer = styled(Stack)`
  padding-bottom: 1rem;
  gap: 1rem;
  @media (min-width: 768px) {
    flex-direction: row;
    gap: 0;
  }
`;

export const PickupDeliveryContainer = styled(Stack)`
  @media (min-width: 768px) {
    flex-basis: 100%;
    padding: 1rem;
  }
`;

export const PickupDeliveryDetailWrapper = styled(Stack)`
  padding-bottom: 2rem;
`;

export const AddressViewWrapper = styled(Stack)`
  font-size: ${(props): string => props.theme.fontSizes.md};
`;

export const IdAndStatusWrapper = styled(Stack)`
  align-items: center;
`;

export const VesselWrapper = styled(Stack)`
  margin-bottom: 0.35rem;
`;

export const CollapsibleText = styled.span`
  font-size: ${(props): string => props.theme.fontSizes.sm};
  color: ${(props): string => props.theme.colors.primary_600};
  text-decoration: underline;
  align-self: flex-start;
  cursor: pointer;
`;
